export interface Dictionary<T> {
  [index: string]: T;
}

export const omit = (obj, props: string[]) => {
  const clone = { ...obj };
  props.forEach((prop) => delete clone[prop]);
  return clone;
};

export const chunk = (arr, chunkSize = 1, cache = []) => {
  const tmp = [...arr];
  if (chunkSize <= 0) {
    return cache;
  }
  while (tmp.length) {
    cache.push(tmp.splice(0, chunkSize));
  }
  return cache;
};

export const difference = <T>(arr1: T[], arr2: T[]) => arr1.filter((x) => !arr2.includes(x));

export const has = (obj, path) => {
  // Regex explained: https://regexr.com/58j0k
  const pathArray = Array.isArray(path) ? path : path.match(/([^[.\]])+/g);

  return !!pathArray.reduce((prevObj, key) => prevObj && prevObj[key], obj);
};

export const isObject = function (obj) {
  return Object.prototype.toString.call(obj) === '[object Object]';
};

export const uniqBy = (arr, iteratee) => {
  if (typeof iteratee === 'string') {
    const prop = iteratee;
    iteratee = (item) => item[prop];
  }

  return arr.filter((x, i, self) => i === self.findIndex((y) => iteratee(x) === iteratee(y)));
};

export const without = (arr, ...args) => arr.filter((item) => !args.includes(item));

const binaryXor = (arr1, arr2) => {
  const a = arr1.filter((x) => !arr2.includes(x));
  const b = arr2.filter((x) => !arr1.includes(x));

  return Array.from(new Set([...a, ...b]));
};
export const xor = (...arrays) => arrays.reduce(binaryXor, []);

export const pick = <T extends object, K extends keyof T>(obj: T, whiteList: K | K[]): Pick<T, K> =>
  Object.assign({}, ...(Array.isArray(whiteList) ? whiteList : [whiteList]).map((key) => ({ [key]: obj[key] })));

export const pickBy = <T extends object>(object: T, predicate: (parameter) => boolean): Partial<T> =>
  Object.keys(object).reduce((prev, x) => (predicate(object[x]) ? { ...prev, [x]: object[x] } : prev), {});

export const differenceBy = <T, K>(
  array1: readonly T[],
  array2: readonly T[],
  iteratee: (keyof T & string) | ((value: T) => K)
): T[] => {
  const byFn: (val: T) => K = typeof iteratee === 'string' ? (item: T) => item[iteratee] as K : iteratee;

  const differenceSet = new Set(array2.map(byFn));
  return array1.filter((value) => !differenceSet.has(byFn(value)));
};

export const keyBy = <T>(
  collection: readonly T[] | Dictionary<T>,
  iteratee: (keyof T & string) | ((value: T) => string) = (val) => `${val}`
): Dictionary<T> => {
  const byFn: (val: T) => string = typeof iteratee === 'string' ? (item: T): string => `${item[iteratee]}` : iteratee;
  const arr = Array.isArray(collection) ? collection : Object.values(collection);

  return arr.reduce((acc, val) => {
    acc[byFn(val)] = val;
    return acc;
  }, {});
};

export const uniq = <T>(elements: T[]): T[] => [...new Set(elements)];

export const isUndefined = (value?) => value === undefined;

// It's not direct copy of _.isEqual as lodash handles was more cases, to limit numbers of handled cases to reasonable amount use with caution

export function isEqual(obj1, obj2): boolean {
  function getType(obj) {
    return Object.prototype.toString.call(obj).slice(8, -1).toLowerCase();
  }

  function areArraysEqual() {
    if (obj1.length !== obj2.length) {
      return false;
    }

    for (let i = 0; i < obj1.length; i++) {
      if (!isEqual(obj1[i], obj2[i])) {
        return false;
      }
    }

    return true;
  }

  function areObjectsEqual() {
    if (Object.keys(obj1).length !== Object.keys(obj2).length) {
      return false;
    }

    for (const key in obj1) {
      if (Object.prototype.hasOwnProperty.call(obj1, key)) {
        if (!isEqual(obj1[key], obj2[key])) {
          return false;
        }
      }
    }

    return true;
  }

  function areFunctionsEqual() {
    return obj1.toString() === obj2.toString();
  }

  function areNumberEqual() {
    return (isNaN(obj1) && isNaN(obj2)) || obj1 === obj2;
  }

  function arePrimativesEqual() {
    return obj1 === obj2;
  }

  const type = getType(obj1);

  if (type !== getType(obj2)) {
    return false;
  }

  if (obj1 === obj2) {
    return true;
  }

  if (type === 'array') {
    return areArraysEqual();
  }
  if (type === 'object') {
    return areObjectsEqual();
  }
  if (type === 'function') {
    return areFunctionsEqual();
  }
  if (type === 'number') {
    return areNumberEqual();
  }
  return arePrimativesEqual();
}

export const isEmpty = (obj) => [Object, Array].includes((obj || {}).constructor) && !Object.entries(obj || {}).length;

export const sortBy = <T>(
  arr: readonly T[],
  iteratee: (keyof T & string) | ((value: T) => unknown) = (val) => val
): T[] => {
  const byFn = typeof iteratee === 'string' ? (item) => item[iteratee] : iteratee;

  return arr
    .map((value, index) => ({
      value: value,
      index: index++,
      criteria: byFn(value),
    }))
    .sort((left, right) => {
      const a = left.criteria;
      const b = right.criteria;
      if (a !== b) {
        if (a > b || a === void 0) {
          return 1;
        }
        if (a < b || b === void 0) {
          return -1;
        }
      }
      return left.index - right.index;
    })
    .map(({ value }) => value);
};

export function groupBy<T>(values: T[], iteratee: (keyof T & string) | ((value: T) => unknown)): Dictionary<T[]> {
  const byFn: (val: T) => unknown = typeof iteratee === 'string' ? (item: T): unknown => item[iteratee] : iteratee;

  return values.reduce((acc, val) => {
    const group = `${byFn(val)}`;
    acc[group] = acc[group] || [];
    acc[group].push(val);
    return acc;
  }, {});
}

export function sample<T>(values: T[]): T {
  return values[Math.floor(Math.random() * values.length)];
}

export function cloneDeep<T>(object: T): T {
  return JSON.parse(JSON.stringify(object)) as T;
}

export function debounce<T extends (...args: unknown[]) => unknown>(
  fn: T,
  delay = 0
): (...args: Parameters<T>) => void {
  let timer: ReturnType<typeof setTimeout> | null = null;

  return function (...args: Parameters<T>): void {
    if (timer) {
      clearTimeout(timer);
    }

    timer = setTimeout(() => {
      fn(...args);
    }, delay);
  };
}

export function uniqueBy<T>(arr: T[], iteratee: (value: T) => unknown): T[] {
  const set = new Set<unknown>();
  return arr.filter((x) => {
    const value = iteratee(x);
    if (set.has(value)) {
      return false;
    }
    set.add(value);
    return true;
  });
}
